import { MapView, WebMercatorViewport } from '@deck.gl/core/typed';
import DeckGL from '@deck.gl/react/typed';
import {
  useGetAnchorageMutation,
  useGetMapSnapshotMutation,
  useGetPortTargetCatalogMutation,
  useGetTargetsMutation,
  useGetTerminalTargetCatalogMutation,
  useGetTerminalsOnMapMutation,
  useGetVesselCatalogInAreaMutation,
} from 'app/api/facade';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useOnClickOutside,
  useUpdateBbox,
  useGetMapSettingsState,
  useLocalStorage,
  useAlertHook,
  useGetAnalyticsLayer,
  useRulerTools,
} from 'app/hooks';
import { ViewStateChangeParameters } from '@deck.gl/core/typed/controllers/controller';

import {
  PortCard,
  MapControlPanel,
  BboxVesselsModal,
  FilterPanel,
  VesselsCatalogModal,
  AreaCard,
  HistoryDatePicker,
  TerminalCard,
  Icon,
} from 'app/components';
import {
  changePredictedRouteVisibility,
  setFindTarget,
  setRouteForSelectedVessel,
  setSelectedTarget,
} from 'app/utils/selectedTargetsSlice';
import { useAppDispatch, useAppSelector } from 'app/store/store';
import { useGetPortsMutation } from 'app/api/marine-infrastructure';

import { PortsTooltips } from './PortsTooltips';
import { TerminalsTooltips } from './TerminalsTooltips';
import { useLayerTargetSelected } from './Layers/useLayerTargetSelected';
import { useLayerTargetHovered } from './Layers/useLayerTargetHovered';
import { useLayerVessels } from './Layers/useLayerVessels';
import { useLayerPorts } from './Layers/useLayerPorts';
import { useLayerRoute } from './Layers/useLayerRoute';
import { setFilters, setIsVesselPLaybackActive } from 'app/utils/mapStateSlice';
import { useTileLayer } from './Layers/TileLayer';
import { useLayerAreas } from './Layers/useLayerAreas';
import { getAreaBounds, getTooltipDeckGl, glToStringHDMS, useBboxValidation } from 'app/helpers';
import { setShowAreaCard } from 'app/utils/marineInfrastructureReducer';
import moment from 'moment';
import { getI18n, useTranslation } from 'react-i18next';

import { useLayerTerminals } from './Layers/useLayerTerminals';
import { Filters } from 'app/types/types';
import { SerializedError } from '@reduxjs/toolkit';
import { useLayerRoutePredicted } from './Layers/useLayerRoutePredicted';
import { useLayerRoutePlanned } from './Layers/useLayerRoutePlanned';
import { setShowRouteBuilded } from 'app/utils/facadeReducer';
import { useLayerWind } from './Layers/useLayerWind';
import { useTemperatureLayer } from './Layers/useTemperatureLayer';
import { useWavesLayer } from './Layers/useWavesLayer';
import { useLayerSpecialAreas } from './Layers/useLayerSpecialAreas';
import { useLayerAtons } from './Layers/useLayerAtons';
import { useLayerSeaMarks } from './Layers/useLayerSeaMarks';
import { AISCard } from 'VesselCard/AISCard';
import { MapInfo } from 'MapInfo/MapInfo';
import { vesselsCatalogModalColumns } from 'app/components';
import { AtonCard } from 'AtonCard/AtonCard';
import { usePlaybackLayer } from './Layers/usePlaybackLayer';

import styles from './DeckGlMap.module.scss';
import { useLayerAnchorage } from './Layers/useLayerAnchorage';
import { useLayerPlaybackVessel } from './Layers/useLayerPlaybackVessel';
import { usePressureLayer } from './Layers/usePressureLayer';

const getZoom = (type: 'port' | 'vessel' | 'terminal' | 'aton') => {
  switch (type) {
    case 'vessel':
    case 'aton':
      return 6;
    case 'port':
      return 7;
    case 'terminal':
      return 9;
    default:
      return 9;
  }
};
export const DeckGlMap = ({ center, zoom, onError }: { center: number[]; zoom: number; onError: (e: Error) => void }) => {
  const { t } = useTranslation();
  const { addAlert } = useAlertHook();
  const profile = useAppSelector((state) => state.userSlice.profile);
  const timeFormat = useMemo(() => profile?.ui?.['timeFormat'] || 'HH:mm:ss', [profile]);
  const isLocalTz = useMemo(() => profile?.ui?.['isLocalTz'] === 'true', [profile]);
  const coordinatesFormat = useMemo(() => profile?.ui?.['coordinatesFormat'] ?? 'DMS', [profile]);
  const isVesselPlaybackActive = useAppSelector((state) => state.mapStateSlice.isVesselPlaybackActive);

  const language = useMemo(() => getI18n().language, [t]);
  const mode = useAppSelector((state) => state.mapStateSlice.mode);

  const rulerOn = useAppSelector((state) => state.mapStateSlice.tools.ruler);
  let clickCount = 0;

  const isHistoryMode = mode === 'history';
  const isAnalyticsMode = mode === 'analytics';
  const isPlaybackMode = mode === 'playback';
  const isAreasMode = mode === 'areas';
  const isRouteBuilderMode = mode === 'routes';
  const isWeatherLayerHiddenMode = mode === 'history' || mode === 'playback' || mode === 'analytics';
  const { mode: analyticsMode } = window._env_.ANALYTICS_CONFIG ? JSON.parse(window._env_.ANALYTICS_CONFIG) : '';

  const bboxVesselsRef = useRef<HTMLDivElement>(null);
  const filterPanelRef = useRef<HTMLDivElement>(null);

  const dispatch = useAppDispatch();
  const updateBbox = useUpdateBbox();
  const { isValidBbox } = useBboxValidation();

  const [getVessels, vesselsRequest] = useGetTargetsMutation({
    fixedCacheKey: 'get-map-vessels',
  });

  const [getPorts] = useGetPortsMutation({
    fixedCacheKey: 'get-map-ports',
  });

  const [getTerminals] = useGetTerminalsOnMapMutation({
    fixedCacheKey: 'get-terminals-mutation',
  });

  const [getAtons, atonsRequest] = useGetTargetsMutation({
    fixedCacheKey: 'get-map-atons',
  });

  const [getAnchorage] = useGetAnchorageMutation({
    fixedCacheKey: 'get-map-anchorage',
  });

  const [getSnapshotVessles, { data: snapshotVesselsResponse, ...snapshotRequest }] = useGetMapSnapshotMutation({
    fixedCacheKey: 'get-map-snapshot-mutation',
  });

  useEffect(() => {
    if (snapshotRequest.isSuccess) {
      if (isHistoryMode && selectedTarget && Object.keys(selectedTarget).length !== 0) {
        const res = snapshotVesselsResponse?.results?.find((vessel) => vessel.mmsi === selectedTarget.target?.object.mmsi);
        if (res !== undefined) {
          dispatch(setSelectedTarget({ target: { object: res }, type: 'vessel' }));
        } else {
          dispatch(setSelectedTarget(null));
        }
      }
    }
  }, [snapshotRequest.isSuccess, snapshotRequest.isLoading]);

  const vesselsInPortMutation = useGetPortTargetCatalogMutation();
  const vesselsInTerminalMutation = useGetTerminalTargetCatalogMutation();
  const vesselsInAreaMutation = useGetVesselCatalogInAreaMutation();

  const debouncedGetVesselsHandler = useCallback(debounce(getVessels, 400), []);
  const debouncedGetPortsHandler = useCallback(debounce(getPorts, 400), []);
  const debouncedGetTerminalsHandler = useCallback(debounce(getTerminals, 400), []);
  const debouncedGetAtonsHandler = useCallback(debounce(getAtons, 400), []);
  const debouncedGetAnchorageHandler = useCallback(debounce(getAnchorage, 700), []);

  const debounceSnapshotGetVesselsHandler = useCallback(debounce(getSnapshotVessles, 400), []);

  const filters = useAppSelector((state) => state.mapStateSlice.filters);
  const [localFilter, setLocalFilter] = useLocalStorage<Filters | null>('filters-vesselsOnMap', null);
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [disabledMode, setDisabledMode] = useState<boolean>(false);

  useEffect(() => {
    if (localFilter !== null) {
      dispatch(setFilters(localFilter));
    }
  }, [localFilter]);

  useEffect(() => {
    let error;
    if (atonsRequest.isError) {
      error = atonsRequest.error;
    }
    if (vesselsRequest.isError) {
      error = vesselsRequest.error;
    }
    if (error) {
      addAlert(error);
    }
  }, [atonsRequest.isError, vesselsRequest.isError]);
  const ports = useAppSelector((state) => state.facadeSlice.portsOnMap);
  const terminals = useAppSelector((state) => state.facadeSlice.terminalsOnMap);
  const bbox = useAppSelector((state) => state.mapStateSlice.bbox);
  const uiconfig = useAppSelector((state) => state.userSlice.uiConfig);
  const routeBuilded = useAppSelector((state) => state.facadeSlice.routeBuilded);

  const initial: Record<string, number> = useMemo(() => ({ longitude: center[0], latitude: center[1], zoom, minZoom: 1 }), []);

  const [viewState, setViewState] = useState<Record<string, number>>({ ...initial });
  const [showPortVessels, setShowPortVessels] = useState<boolean>(false);
  const [showTerminalVessels, setShowTerminalVessels] = useState<boolean>(false);

  const [showAreaVessels, setShowAreaVessels] = useState<boolean>(false);

  const selectedTarget = useAppSelector((state) => state.selectedTargetsSlice?.selectedTarget);
  const findTarget = useAppSelector((state) => state.selectedTargetsSlice?.findTarget);
  const selectedArea = useAppSelector((state) => state.marineInfrastructureSlice.selectedArea);
  const [selectedAreaId, setSelectedAreaId] = useState<string | null>(null);

  const mapSettingsStates = useGetMapSettingsState();
  const {
    showVesselsState,
    showVesselsNamesState,
    showPortsState,
    showTerminalsState,
    showAtonsState,
    showSeaMarksState,
    showLegendState,
    showRouteState,
    showIndicatorState,
    mapConfigState,
    showRoutePredictedState,
    checkBoxSpecialAreasState,
    showAnchorageState,
  } = mapSettingsStates;

  const [showVessels] = showVesselsState;
  const [showVesselsNames] = showVesselsNamesState;
  const [showPorts] = showPortsState;
  const [showIndicator] = showIndicatorState;
  const [showTerminals] = showTerminalsState;
  const [showAtons] = showAtonsState;
  const [showSeaMarks] = showSeaMarksState;
  const [showAnchorage] = showAnchorageState;

  const [showRoute] = showRouteState;
  const [showRoutePredicted] = showRoutePredictedState;

  const [showBboxVessels, setShowBboxVessels] = useState<boolean>(false);
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [mouseCoordinates, setMouseCoordinates] = useState<string>('— — — N — — — E');

  const layerTile = useTileLayer(mapConfigState);
  const layerSeaMarks = useLayerSeaMarks(showSeaMarks);
  const layerTargetSelected = useLayerTargetSelected(showIndicator);
  const layerTargetHovered = useLayerTargetHovered();
  const layerVessels = useLayerVessels(
    showVessels && !isVesselPlaybackActive,
    showIndicator,
    isHistoryMode,
    showVesselsNames,
    viewState,
  );
  const layerPorts = useLayerPorts(viewState, showPorts);
  const layerTerminals = useLayerTerminals(viewState, showTerminals);
  const layerAtons = useLayerAtons(showIndicator, showAtons);
  const routeLayer = useLayerRoute(isHistoryMode);
  const layerRoutePredicted = useLayerRoutePredicted(isHistoryMode);
  const layerRoutePlanned = useLayerRoutePlanned(isRouteBuilderMode);
  const layerwWind = useLayerWind(isWeatherLayerHiddenMode);
  const layerTemp = useTemperatureLayer(bbox, viewState, isWeatherLayerHiddenMode);
  const layerWaves = useWavesLayer(bbox, viewState, isWeatherLayerHiddenMode);
  const layerPressure = usePressureLayer(bbox, viewState, isWeatherLayerHiddenMode);
  const layerAnalytics = useGetAnalyticsLayer(viewState, isAnalyticsMode, bbox, setShowLoader, setDisabledMode, analyticsMode);
  const layerPlaybackVessel = useLayerPlaybackVessel(viewState, (areasBounds: number[], zoom?: number) => {
    const viewport = new WebMercatorViewport({
      ...viewState,
    });

    const newViewport = viewport.fitBounds([
      [areasBounds[0], areasBounds[1]],
      [areasBounds[2], areasBounds[3]],
    ]);

    setViewState((prev) => ({
      ...prev,

      longitude: newViewport.longitude,
      latitude: newViewport.latitude,
      zoom: newViewport.zoom > (zoom || 9) ? zoom || 9 : newViewport.zoom,
      transitionDuration: 500,
    }));
  });
  const { layer: layerAreas, data: areaData, isLoading: areaIsLoading } = useLayerAreas(isAreasMode);
  const layerSpecialAreas = useLayerSpecialAreas(checkBoxSpecialAreasState, selectedAreaId, setSelectedAreaId);
  const { layer: layerAnchorage } = useLayerAnchorage(viewState, showAnchorage);

  const {
    layer: layerPlayback,
    playbackPicker,
    isZoomEnabled,
  } = usePlaybackLayer(zoom, viewState, isPlaybackMode, setShowLoader);

  const { layer: layerRuler, handleClick: handleClickRuler, handleHover: handleHoverRuler } = useRulerTools();

  const [areasBounds, setAreasBounds] = useState<number[] | null>(null);

  const [showFormularVessel, setShowFormularVessel] = useState<boolean>(false);
  const [showFormularAton, setShowFormularAton] = useState<boolean>(false);
  const [showFormularPort, setShowFormularPort] = useState<boolean>(false);
  const [showFormularTerminal, setShowFormularTerminal] = useState<boolean>(false);

  const handleViewChange = useCallback(
    (
      param: ViewStateChangeParameters & {
        viewId: string;
      },
    ) => {
      if (!isAnalyticsMode) {
        setViewState({
          ...param.viewState,
          minZoom: 1,
        });
        localStorage.setItem('center', JSON.stringify([param.viewState.longitude, param.viewState.latitude]));
        localStorage.setItem('zoom', JSON.stringify(param.viewState.zoom));
      }
      if (isAnalyticsMode && analyticsMode === 'static') {
        setViewState(param.viewState);
        localStorage.setItem('center', JSON.stringify([param.viewState.longitude, param.viewState.latitude]));
        localStorage.setItem('zoom', JSON.stringify(param.viewState.zoom));
        const viewport = new WebMercatorViewport(param.viewState);
        updateBbox(viewport);
      }
      if (isAnalyticsMode && analyticsMode === 'dynamic') {
        const { longitude, latitude, zoom } = param.viewState;
        const { width, height } = viewState;
        const viewport = new WebMercatorViewport({
          longitude,
          latitude,
          zoom,
          width,
          height,
        });

        const bounds = viewport.getBounds();
        let borderLongitude = longitude;

        if (bounds[0] < -179) {
          borderLongitude = longitude + (-179 - bounds[0]);
        } else if (bounds[2] > 179) {
          borderLongitude = longitude - (bounds[2] - 179);
        }

        const borderViewport = viewport.fitBounds([
          [-179, 21],
          [179, 21],
        ]);

        setViewState({
          ...param.viewState,
          longitude: borderLongitude,
          minZoom: borderViewport.zoom,
        });
      }
    },
    [viewState],
  );

  const handleMapResize = () => {
    const viewport = new WebMercatorViewport(viewState);
    updateBbox(viewport);
  };

  const handleMapLoad = () => {
    if (selectedTarget?.target && findTarget) {
      setViewState((prev) => ({
        ...prev,
        longitude: selectedTarget.target.object?.lon || selectedTarget.target.object?.position?.longitude,
        latitude: selectedTarget.target.object?.lat || selectedTarget.target.object?.position?.latitude,
        zoom: getZoom(selectedTarget?.type),
        transitionDuration: 500,
      }));
      dispatch(setFindTarget(false));
    }
  };

  const goToArea = useCallback(
    (areasBounds: number[], zoom?: number) => {
      const viewport = new WebMercatorViewport({
        ...viewState,
      });

      const newViewport = viewport.fitBounds([
        [areasBounds[0], areasBounds[1]],
        [areasBounds[2], areasBounds[3]],
      ]);

      setViewState((prev) => ({
        ...prev,

        longitude: newViewport.longitude,
        latitude: newViewport.latitude,
        zoom: newViewport.zoom > (zoom || 9) ? zoom || 9 : newViewport.zoom,
        transitionDuration: 500,
      }));
    },
    [areasBounds],
  );
  const [startDate, setStartDate] = useState(new Date());

  const snapshotData = useMemo(() => {
    if (isHistoryMode && selectedTarget && Object.keys(selectedTarget).length !== 0) {
      return snapshotVesselsResponse?.results?.find((vessel) => {
        return selectedTarget.target.object.mmsi === vessel.mmsi;
      });
    } else {
      return undefined;
    }
  }, [snapshotVesselsResponse, mode, selectedTarget]);

  const clickOutsideHandler = () => {
    setShowPortVessels(false);
  };
  useOnClickOutside(bboxVesselsRef, clickOutsideHandler);

  const view = new MapView({
    repeat: true,
    controller: {
      scrollZoom: {
        smooth: true,
      },
    },
  });

  const onDateSelect = useCallback(
    (date: string) => {
      const timestamp = moment(date).utc().format('YYYY-MM-DDTHH:mm:ssZ');
      snapshotRequest.reset();
      if (bbox) {
        debounceSnapshotGetVesselsHandler({
          value: {
            bbox: bbox,
            timestamp,
          },
        });
      }
    },
    [startDate, bbox],
  );

  useEffect(() => {
    if (typeof center[0] === 'number' && typeof center[1] === 'number') {
      setViewState({
        longitude: center[0],
        latitude: center[1],
        transitionDuration: 250,
        zoom,
      });
    }
  }, [center]);

  useEffect(() => {
    dispatch(setShowAreaCard(isAreasMode));
    if (!isAreasMode) {
      setSelectedAreaId(null);
    }
  }, [isAreasMode]);

  useEffect(() => {
    if (!isAnalyticsMode && !isPlaybackMode) {
      setShowLoader(false);
    }
    if (isAnalyticsMode && analyticsMode === 'dynamic') {
      const viewport = new WebMercatorViewport({
        ...viewState,
      });

      /*
        Получаем borderViewport, который будет вмещать в себя всю карту по долготе (от -180 до 180).
         Значения широты выбраны таким образом, чтобы карта располагалась примерно по центру.
         С помощью данного viewport будут сформированы границы, которые ограничивают карту слева и справа,
         а также динамичное значение minZom (для разных экранов будет одинаковое расположение карты из-за
         разного значения minZom), чтобы
         при уменьшении зума не было выхода за границы карты
      */
      const borderViewport = viewport.fitBounds([
        [-179, 21],
        [179, 21],
      ]);

      setViewState((prev) => ({
        ...prev,
        longitude: borderViewport.longitude,
        latitude: borderViewport.latitude,
        zoom: borderViewport.zoom > 1 ? borderViewport.zoom : 1,
        minZom: borderViewport.zoom > 1 ? borderViewport.zoom : 1,
      }));
      updateBbox(borderViewport);
    }
  }, [isAnalyticsMode, isPlaybackMode]);

  useEffect(() => {
    const viewport = new WebMercatorViewport(viewState);
    updateBbox(viewport);
  }, [viewState]);

  useEffect(() => {
    if (Object.keys(bbox).length > 0 && !isAnalyticsMode && !isPlaybackMode) {
      if (showVessels) {
        if (isHistoryMode) {
          const timestamp = moment(startDate).utc().format('YYYY-MM-DDTHH:mm:ssZ');

          debounceSnapshotGetVesselsHandler({
            value: {
              bbox,
              timestamp,
            },
          });
        } else {
          const constructionDate =
            filters.constructionDate?.from !== undefined || filters.constructionDate?.to !== undefined
              ? {
                  from: filters.constructionDate?.from
                    ? moment.utc({ year: Number(filters.constructionDate.from) }).format('YYYY-MM-DDTHH:mm:ssZ')
                    : undefined,
                  to: filters.constructionDate?.to
                    ? moment.utc({ year: Number(filters.constructionDate.to) }).format('YYYY-MM-DDTHH:mm:ssZ')
                    : undefined,
                }
              : undefined;

          const constructionDateDatetime = filters.constructionDateDatetime ? filters.constructionDateDatetime : undefined;

          const eta =
            filters.eta?.from !== undefined || filters.eta?.to !== undefined
              ? {
                  from: filters.eta?.from ? `${filters.eta?.from}T00:00:00Z` : undefined,
                  to: filters.eta?.to ? `${filters.eta?.to}T00:00:00Z` : undefined,
                }
              : undefined;

          const etaDatetime = filters.etaDatetime ? filters.etaDatetime : undefined;

          debouncedGetVesselsHandler({
            value: {
              bbox,
              fleetOnly: uiconfig?.viewportFleetOnly || filters?.fleetOnly,
              source: filters?.sourceAll ? [] : filters?.source,
              type: filters?.typeAll ? [] : filters?.type,
              country: filters?.countryAll ? [] : filters?.country,
              status: filters?.statusAll ? [] : filters?.status,
              fleetGroup: !filters?.fleetOnly || filters?.fleetGroupAll ? [] : filters?.fleetGroup,
              specialType: ['NONE'],
              sog: filters?.sog,
              cog: filters?.cog,
              heading: filters?.heading,
              draught: filters?.draught,
              eta: eta || {},
              etaDatetime: etaDatetime || undefined,
              constructionDateDatetime: constructionDateDatetime || undefined,
              inPortKey: filters?.inPortKey || [],
              inAreaCode: filters?.inAreaCode || [],
              destinationPortKey: filters?.destinationPortKey || [],
              constructionDate: constructionDate || {},
            },
          });
        }
      }
    }
  }, [filters, bbox, showVessels, language, isHistoryMode]);

  useEffect(() => {
    if (Object.keys(bbox).length > 0 && !isAnalyticsMode && !isPlaybackMode) {
      if (showAtons) {
        const constructionDate =
          filters.constructionDate?.from !== undefined || filters.constructionDate?.to !== undefined
            ? {
                from: filters.constructionDate?.from
                  ? moment.utc({ year: Number(filters.constructionDate.from) }).format('YYYY-MM-DDTHH:mm:ssZ')
                  : undefined,
                to: filters.constructionDate?.to
                  ? moment.utc({ year: Number(filters.constructionDate.to) }).format('YYYY-MM-DDTHH:mm:ssZ')
                  : undefined,
              }
            : undefined;

        const eta =
          filters.eta?.from !== undefined || filters.eta?.to !== undefined
            ? {
                from: filters.eta?.from ? `${filters.eta?.from}T00:00:00Z` : undefined,
                to: filters.eta?.to ? `${filters.eta?.to}T00:00:00Z` : undefined,
              }
            : undefined;
        debouncedGetAtonsHandler({
          value: {
            bbox,
            fleetOnly: uiconfig?.viewportFleetOnly || filters?.fleetOnly,
            source: filters?.sourceAll ? [] : filters?.source,
            type: filters?.typeAll ? [] : filters?.type,
            country: filters?.countryAll ? [] : filters?.country,
            status: filters?.statusAll ? [] : filters?.status,
            fleetGroup: !filters?.fleetOnly || filters?.fleetGroupAll ? [] : filters?.fleetGroup,
            specialType: ['ATON'],
            sog: filters?.sog,
            cog: filters?.cog,
            heading: filters?.heading,
            draught: filters?.draught,
            eta: eta || {},
            inPortKey: filters?.inPortKey || [],
            inAreaCode: filters?.inAreaCode || [],
            destinationPortKey: filters?.destinationPortKey || [],
            constructionDate: constructionDate || {},
          },
        });
      }
    }
  }, [filters, bbox, showAtons, language]);

  useEffect(() => {
    if (Object.keys(bbox).length > 0 && !isAnalyticsMode) {
      if (showPorts && viewState.zoom > 6) {
        debouncedGetPortsHandler({
          value: {
            bbox,
          },
        });
      }
    }
  }, [bbox, showPorts, viewState.zoom]);

  useEffect(() => {
    if (Object.keys(bbox).length > 0 && !isAnalyticsMode) {
      if (showTerminals && viewState.zoom > 8) {
        debouncedGetTerminalsHandler({
          value: {
            bbox,
          },
        });
      }
    }
  }, [bbox, showTerminals, viewState.zoom]);

  useEffect(() => {
    if (!selectedTarget) {
      dispatch(setRouteForSelectedVessel({ isShown: false, route: [], routePoints: [] }));
      return;
    }
    if (selectedTarget.target?.object && Object.keys(selectedTarget.target.object).length !== 0) {
      if (selectedTarget.type === 'vessel') {
        setShowFormularPort(false);
        setShowFormularTerminal(false);
        setShowFormularAton(false);

        setShowFormularVessel(true);
        dispatch(setRouteForSelectedVessel({ isShown: showRoute }));
        dispatch(changePredictedRouteVisibility(showRoutePredicted));
      }
      if (selectedTarget.type === 'aton') {
        setShowFormularPort(false);
        setShowFormularTerminal(false);
        setShowFormularVessel(false);

        setShowFormularAton(true);

        dispatch(setRouteForSelectedVessel({ isShown: showRoute }));
        dispatch(changePredictedRouteVisibility(showRoutePredicted));
      }
      if (selectedTarget.type === 'port') {
        setShowFormularPort(true);
        setShowFormularVessel(false);
        setShowFormularAton(false);
        setShowFormularTerminal(false);
      }
      if (selectedTarget.type === 'terminal') {
        setShowFormularPort(false);
        setShowFormularVessel(false);
        setShowFormularAton(false);
        setShowFormularTerminal(true);
      }
    }
  }, [selectedTarget]);

  useEffect(() => {
    if (selectedArea && 'geometry' in selectedArea) {
      const bounds = getAreaBounds(selectedArea);
      if (bounds) {
        setAreasBounds(bounds);
      }
    }
  }, [selectedArea]);

  useEffect(() => {
    if (areasBounds && isAreasMode) {
      goToArea(areasBounds);
    }
  }, [areasBounds]);

  useEffect(() => {
    if (isRouteBuilderMode && routeBuilded.route && routeBuilded.route.geometry) {
      const bounds = getAreaBounds(routeBuilded.route);

      if (bounds) {
        goToArea(bounds, 12);
        dispatch(setShowRouteBuilded(false));
      }
    }
  }, [routeBuilded]);

  useEffect(() => {
    if (snapshotRequest.isUninitialized && isHistoryMode && language && showVessels && bbox) {
      const timestamp = moment(startDate).utc().format('YYYY-MM-DDTHH:mm:ssZ');

      debounceSnapshotGetVesselsHandler({
        value: {
          bbox: bbox,
          timestamp,
        },
      });
    }
    if (snapshotRequest.error) {
      const error: SerializedError | any = snapshotRequest.error;

      addAlert({
        data: {
          messages: [{ description: error.data.messages?.[0].description, type: error.data.messages?.[0].type }],
        },
      });
      setStartDate(new Date());
    }
  }, [snapshotRequest.isUninitialized, snapshotRequest.isError]);

  useEffect(() => {
    if (!isAnalyticsMode && Object.keys(bbox).length > 0 && showAnchorage && viewState.zoom > 6) {
      debouncedGetAnchorageHandler({
        bbox: bbox,
      });
    }
  }, [bbox, showAnchorage, viewState.zoom]);

  useEffect(() => {
    if (!isZoomEnabled && isPlaybackMode && viewState.zoom < 10) {
      setViewState((prev) => ({
        ...prev,
        zoom: 11,
        transitionDuration: 250,
      }));
    }
  }, [isPlaybackMode, isZoomEnabled, viewState.zoom]);

  return (
    <>
      {isHistoryMode && <HistoryDatePicker setDate={setStartDate} selected={startDate} onDateSelect={onDateSelect} />}
      {isPlaybackMode && playbackPicker}

      <DeckGL
        viewState={viewState}
        views={view}
        style={{ position: 'relative' }}
        onResize={handleMapResize}
        onLoad={handleMapLoad}
        onViewStateChange={handleViewChange}
        onError={onError}
        controller={
          isPlaybackMode
            ? {
                dragRotate: false,
                dragPan: isZoomEnabled,
                doubleClickZoom: isZoomEnabled,
                scrollZoom: isZoomEnabled,
              }
            : {
                dragRotate: false,
                doubleClickZoom: !rulerOn,
              }
        }
        getCursor={(state) => {
          if (state.isHovering) return 'pointer';
          if (state.isDragging) return 'grab';
          return '';
        }}
        layers={[
          layerTile,
          layerTemp,
          layerWaves,
          layerPressure,
          layerAnchorage,
          layerSeaMarks,
          layerwWind,
          layerSpecialAreas,
          layerAreas,
          layerPorts,
          layerVessels,
          layerAnalytics,
          routeLayer,
          layerRoutePredicted,
          layerRoutePlanned,
          layerTerminals,
          layerAtons,
          layerTargetSelected,
          layerTargetHovered,
          layerRuler,
          layerPlayback,
          layerPlaybackVessel,
        ]}
        onClick={(info, e) => {
          if (rulerOn) {
            clickCount++;
            if (clickCount === 1) {
              setTimeout(function () {
                clickCount = 0;
                handleClickRuler(info);
              }, 300);
            }
          } else {
            if (!info.layer && (e.target.id === 'deckgl-wrapper' || e.target.id === 'view-MapView')) {
              if (!isAnalyticsMode) {
                dispatch(setSelectedTarget(null));
              }
              if (!isAreasMode) {
                dispatch(setShowAreaCard(false));
                setSelectedAreaId(null);
              }
            }
          }
        }}
        onHover={(info) => {
          if (info.viewport) {
            setMouseCoordinates(glToStringHDMS(info.viewport?.unproject(info.pixel || [0, 0]), coordinatesFormat));
            handleHoverRuler(info);
          }
        }}
        getTooltip={(info) => getTooltipDeckGl(info, viewState.zoom, timeFormat, isLocalTz)}
      >
        {isRouteBuilderMode && (
          <div className={styles.warning_container}>
            <div className={styles.warning}>
              <p>{t('RouteBuilderWarning')}</p>
            </div>
          </div>
        )}
        {!isAnalyticsMode && showPorts && <PortsTooltips ports={ports} viewState={viewState} />}
        {showTerminals && <TerminalsTooltips terminals={terminals} viewState={viewState} />}

        <MapInfo showLegendState={showLegendState} />
      </DeckGL>
      {showLoader && (isAnalyticsMode || isPlaybackMode) && isValidBbox(bbox) && (
        <div className={styles.loader}>
          <Icon name="loader" className={styles.spinner} />
          {t('DataIsLoading')}
        </div>
      )}
      {disabledMode && isAnalyticsMode && <div className={styles.loader_blocker} />}

      <div className={styles.coordinates}>{mouseCoordinates}</div>

      <MapControlPanel
        isZoomEnabled={isZoomEnabled}
        viewStateControl={[viewState, setViewState]}
        mapSettingsStates={mapSettingsStates}
        setShowBboxVessels={setShowBboxVessels}
        setShowFilter={setShowFilter}
        disabledMode={disabledMode}
      />

      {showBboxVessels && (
        <BboxVesselsModal
          filters={filters}
          bbox={bbox}
          innerRef={bboxVesselsRef}
          onClose={() => {
            setShowBboxVessels(false);
          }}
        />
      )}
      {showFilter && (
        <FilterPanel
          innerRef={filterPanelRef}
          onClose={() => {
            setShowFilter(false);
          }}
          filtersState={[localFilter, setLocalFilter]}
          place="map"
        />
      )}

      {selectedTarget?.target?.object && Object.keys(selectedTarget.target.object).length > 0 ? (
        <>
          {selectedTarget.type === 'vessel' && !isAnalyticsMode && (
            <AISCard
              shown={showFormularVessel}
              mmsi={selectedTarget.target.object.mmsi}
              onClose={() => {
                setShowFormularVessel(false);
                dispatch(setIsVesselPLaybackActive(false));
              }}
              onClick={() => {
                setViewState((prev) => ({
                  ...prev,
                  longitude: selectedTarget.target.object.lon || selectedTarget.target.object?.position?.longitude,
                  latitude: selectedTarget.target.object.lat || selectedTarget.target.object?.position?.latitude,
                  zoom: 7,
                  transitionDuration: 500,
                }));
              }}
              snapshotData={snapshotData}
              isHistoryMode={isHistoryMode}
            />
          )}

          {selectedTarget.type === 'aton' && !isAnalyticsMode && (
            <AtonCard
              isHistoryMode={isHistoryMode}
              shown={showFormularAton}
              mmsi={selectedTarget.target.object.mmsi}
              onClose={() => setShowFormularAton(false)}
              onClick={() => {
                setViewState((prev) => ({
                  ...prev,
                  longitude: selectedTarget.target.object.lon || selectedTarget.target.object?.position?.longitude,
                  latitude: selectedTarget.target.object.lat || selectedTarget.target.object?.position?.latitude,
                  zoom: 7,
                  transitionDuration: 500,
                }));
              }}
              snapshotData={snapshotData}
            />
          )}
          {selectedTarget.type === 'port' && !isAnalyticsMode && (
            <PortCard
              isHistoryMode={isHistoryMode}
              shown={showFormularPort}
              portKey={selectedTarget.target.object.key}
              onClose={() => setShowFormularPort(false)}
              onClick={() => {
                setViewState((prev) => ({
                  ...prev,
                  longitude: selectedTarget.target.object.lon,
                  latitude: selectedTarget.target.object.lat,
                  zoom: 7,
                  transitionDuration: 500,
                }));
              }}
              toggleVesselsCatalog={() => {
                setShowPortVessels((prev) => !prev);
              }}
            />
          )}

          {selectedTarget.type === 'terminal' && !isAnalyticsMode && (
            <TerminalCard
              isHistoryMode={isHistoryMode}
              shown={showFormularTerminal}
              terminalKey={selectedTarget.target.object.key}
              onClose={() => setShowFormularTerminal(false)}
              onClick={() => {
                setViewState((prev) => ({
                  ...prev,
                  longitude: selectedTarget.target.object.lon,
                  latitude: selectedTarget.target.object.lat,
                  zoom: 9,
                  transitionDuration: 500,
                }));
              }}
              toggleVesselsCatalog={() => {
                setShowTerminalVessels((prev) => !prev);
              }}
            />
          )}
          {showPortVessels && selectedTarget.target.object.key && (
            <VesselsCatalogModal
              titles={{
                title: 'PortTargetCatalog',
                titleTotal: 'PortVesselsTotal',
              }}
              requestParam={selectedTarget.target.object.key}
              innerRef={bboxVesselsRef}
              onClose={() => {
                setShowPortVessels(false);
              }}
              vesselsDataMutationResult={vesselsInPortMutation}
              columns={vesselsCatalogModalColumns}
              defaultSortBy={'RECEIVED_DATE'}
            />
          )}
          {showTerminalVessels && selectedTarget.target.object.key && (
            <VesselsCatalogModal
              titles={{
                title: 'TerminalTargetCatalog',
                titleTotal: 'TerminalVesselsTotal',
              }}
              requestParam={selectedTarget.target.object.key}
              innerRef={bboxVesselsRef}
              onClose={() => {
                setShowTerminalVessels(false);
              }}
              vesselsDataMutationResult={vesselsInTerminalMutation}
              columns={vesselsCatalogModalColumns}
              defaultSortBy={'RECEIVED_DATE'}
            />
          )}
        </>
      ) : (
        !isAnalyticsMode && (
          <>
            <AreaCard
              data={areaData}
              isLoading={areaIsLoading}
              onPositionClick={() => {
                if (areasBounds) {
                  goToArea(areasBounds);
                }
              }}
              onClose={() => {
                dispatch(setShowAreaCard(false));
              }}
              toggleVesselsCatalog={() => {
                setShowAreaVessels((prev) => !prev);
              }}
            />
            {showAreaVessels && selectedArea?.code && (
              <VesselsCatalogModal
                titles={{
                  title: 'VesselsInArea',
                  titleTotal: 'AreaVesselsTotal',
                }}
                requestParam={selectedArea.code}
                innerRef={bboxVesselsRef}
                onClose={() => {
                  setShowAreaVessels(false);
                }}
                vesselsDataMutationResult={vesselsInAreaMutation}
                columns={vesselsCatalogModalColumns}
                defaultSortBy={'RECEIVED_DATE'}
              />
            )}
          </>
        )
      )}
    </>
  );
};
